<template>
    <div class="w-full space-y-2 px-px">
        <InputLabel
            v-if="label"
            :value="label"
            :required="required"
            :description="description"
        />
        <div
            :class="[
                'relative flex overflow-hidden ',
                'border transition-shadows duration-input-active',
                {
                    'border-danger hover:border-danger-hover textarea-border-error-focus':
                        error,
                },
                {
                    'border-input-border hover:border-input-hover-border textarea-border-focus':
                        !error && !disabled,
                },
                {
                    'opacity-50 cursor-not-allowed bg-gray-50': disabled,
                },
                roundedClass,
            ]"
        >
            <textarea
                ref="textarea"
                :rows="rows"
                :placeholder="placeholder"
                :class="[
                    'resize-none outline-none border-transparent focus:border-transparent',
                    'placeholder:text-input-placeholder pr-px focus:outline-none w-full text-input transition-colors duration-input-active',
                    {
                        'overflow-hidden': autoAdjustHeight && !maxHeightClass,
                        'overflow-y-auto': autoAdjustHeight && maxHeightClass,
                    },
                    {
                        'pb-5':
                            maxLength &&
                            maxLengthIndicatorPosition === 'inside',
                    },
                    classes,
                    maxHeightClass,
                ]"
                :maxlength="maxLength"
                :value="modelValue"
                @input="onInput"
                :disabled="disabled"
                @keydown.esc="$emit('keydown.esc')"
                @keydown.enter="$emit('keydown.enter')"
                @keyup="onKeyUp"
                @paste="onPaste"
                @focus="(event) => $emit('focus', event)"
                @blur="(event) => $emit('blur', event)"
            />
            <div
                v-if="maxLength"
                :class="[
                    'absolute',
                    {
                        'bottom-0.5 right-2.5':
                            maxLengthIndicatorPosition == 'inside',
                        'top-0 -translate-y-full right-4':
                            maxLengthIndicatorPosition == 'above',
                        'bottom-0.5 translate-y-full right-3':
                            maxLengthIndicatorPosition == 'below',
                    },
                ]"
            >
                <div class="relative">
                    <p
                        :class="[
                            highlightLimitReached
                                ? 'text-danger-active scale-105'
                                : 'text-gray-500',
                            'text-xs transition duration-150 ease-in',
                        ]"
                        v-text="
                            $lang.get(':currentCount/:maxCount characters', {
                                currentCount:
                                    modelValue && modelValue.length
                                        ? modelValue.length
                                        : 0,
                                maxCount: maxLength,
                            })
                        "
                    />
                    <div
                        :class="[
                            highlightLimitReached ? 'opacity-100' : 'opacity-0',
                            'absolute w-[110%] left-1/2 -translate-x-1/2 h-1 top-1/2 -translate-y-1/2 bg-danger rounded-full blur transition-opacity duration-300 ease-in',
                        ]"
                    />
                </div>
            </div>
        </div>
        <InputError v-if="error" :message="error" />
    </div>
</template>

<script>
import InputLabel from '@/Components/FormElements/InputLabel.vue'
import InputError from '@/Components/FormElements/InputError.vue'
import { isInputKey } from '@/Tools/utilities'

export default {
    components: {
        InputLabel,
        InputError,
    },

    emits: [
        'keydown.esc',
        'keydown.enter',
        'keyup',
        'focus',
        'blur',
        'update:modelValue',
    ],

    expose: ['focusMethod', 'blurMethod'],

    props: {
        modelValue: {
            type: [String, null],
            required: true,
        },
        label: {
            type: String,
            default: null,
        },
        description: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        error: {
            type: [String, Array],
            default: null,
        },
        placeholder: {
            type: String,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        rows: {
            type: Number,
            default: 4,
        },
        maxLength: {
            type: Number,
            default: null,
        },
        maxLengthIndicatorPosition: {
            type: String,
            default: 'inside',
            validator(value) {
                return ['inside', 'above', 'below'].includes(value)
            },
        },
        autoAdjustHeight: {
            type: Boolean,
            default: false,
        },
        maxHeightClass: {
            type: [String, null],
            default: null,
        },
        classes: {
            type: [Array, String, Object],
            default: '',
        },
        roundedClass: {
            type: String,
            default: 'rounded-input',
        },
    },

    data() {
        return {
            highlightLimitReached: false,
            highlightTimeout: null,
        }
    },

    mounted() {
        if (this.$refs.textarea.hasAttribute('autofocus')) {
            this.focusMethod()
        }
        this.adjustHeight()
    },

    watch: {
        modelValue(value) {
            this.adjustHeight()
        },
    },

    methods: {
        updateModelValue(event) {
            this.$emit('update:modelValue', event.target.value)
        },

        focusMethod() {
            this.$refs.textarea.focus()
        },

        blurMethod() {
            this.$refs.textarea.blur()
        },

        onInput(event) {
            this.updateModelValue(event)
            // this.adjustHeight()
        },

        adjustHeight() {
            if (this.autoAdjustHeight) {
                this.$refs.textarea.style.height = 'auto'
                if (!this.modelValue) {
                    // Reset by removing the explicit height, letting rows attribute handle it
                    this.$refs.textarea.style.height = ''
                } else {
                    this.$refs.textarea.style.height =
                        this.$refs.textarea.scrollHeight + 2 + 'px'
                }
            }
        },

        onPaste(event) {
            const pastedText = event.clipboardData.getData('text')
            // If there is a limit and that limit is reached and the key pressed is a value that would be input, trigger the animation
            if (
                this.maxLength &&
                ((this.modelValue &&
                    this.modelValue.length >= this.maxLength) ||
                    (typeof pastedText === 'string' &&
                        pastedText.length >= this.maxLength))
            ) {
                this.triggerAnimation()
            }
        },

        onKeyUp(event) {
            this.$emit('keyup', event)

            // If there is a limit and that limit is reached and the key pressed is a value that would be input, trigger the animation
            if (
                this.maxLength &&
                this.modelValue.length >= this.maxLength &&
                isInputKey(event)
            ) {
                this.triggerAnimation()
            }
        },

        triggerAnimation() {
            if (!this.highlightTimeout) {
                this.highlightLimitReached = true
                this.highlightTimeout = setTimeout(() => {
                    this.highlightLimitReached = false
                    this.highlightTimeout = null
                }, 200)
            }
        },
    },
}
</script>
